import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { differenceInSeconds } from "date-fns";
import { useNavigate } from "react-router-dom";

import ConfirmationDialog from "components/ConfirmationDialog";
import { Shared } from "utility/shared";
import { RootState } from "redux/store";
import { AppRoutings } from "utility/enums/app-routings";
import { UserType } from "utility/enums/user-type";
import {
	setLoggedInUserDetail,
	setOrganizationDetail,
} from "redux/authStateSlice";
import { LoginUserDetail } from "models/LoginUserDetail";
import { OrganizationDetail } from "models/OrganizationDetail";
import MsalAuthService from "services/msalAuth.service";
import { CustomHTMLMessage } from "components/CustomHTMLMessage";

enum TimerResetEnum {
	Yes = "true",
	No = "false",
}

export const IdleTimeOutHandler: React.FC = () => {
	let navigate = useNavigate();
	const dispatch = useDispatch();
	const organizationDetails = useSelector(
		(state: RootState) => state.authState.organizationDetails
	);
	const userMenuAccessRights = useSelector(
		(state: RootState) => state.authState.userMenuAccessRights
	);
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	);

	const events = ["load", "scroll", "mousemove", "keydown", "click"];
	const inActivityCheckInterval = 30; // in seconds, every 30 secs check user activity
	const defaultIdleTime = 5; // in minutes
	let inActivityTimer: NodeJS.Timeout | undefined | any = undefined;
	const [openConfirmationDialog, setOpenConfirmationDialog] =
		React.useState<boolean>(false);
	const [isLogout, setLogout] = useState(false);
	const [autoLogoutTimer, setAutoLogoutTimer] = useState<NodeJS.Timer | any>(0);
	const [autoLogoutTimeLeft, setAutoLogoutTimeLeft] = useState(
		inActivityCheckInterval
	);

	useEffect(() => {
		localStorage.setItem(
			Shared.localStorageKeys.lastInteractionTime,
			new Date().toString()
		);
		localStorage.setItem(
			Shared.localStorageKeys.resetSessionTimer,
			TimerResetEnum.No
		);
	}, []);

	const eventHandler = () => {
		if (!isLogout) {
			localStorage.setItem(
				Shared.localStorageKeys.lastInteractionTime,
				new Date().toString()
			);
			if (inActivityTimer) {
				startInActivityTimer();
			}
		}
	};

	const idleTimeLimit = useMemo(
		() => organizationDetails.sessionTimeoutInMinutes || defaultIdleTime,
		[organizationDetails.sessionTimeoutInMinutes]
	); // in Minutes

	useEffect(() => {
		if (
			userDetails &&
			userMenuAccessRights.length > 0 &&
			!isNaN(idleTimeLimit) &&
			userDetails.userTypeId === UserType.Internal
		) {
			addEvents();
		}
		return () => {
			removeEvents();
			clearTimeout(inActivityTimer);
			clearInterval(autoLogoutTimer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		userMenuAccessRights,
		idleTimeLimit,
		userDetails.userTypeId,
		inActivityTimer,
		autoLogoutTimer,
	]);

	const handelLogout = () => {
		removeEvents();
		setOpenConfirmationDialog(false);
		setLogout(true);
		dispatch(setLoggedInUserDetail({} as LoginUserDetail));
		dispatch(setOrganizationDetail({} as OrganizationDetail));
		Shared.clearStorageValues(false);
		clearTimeout(inActivityTimer);
		clearInterval(autoLogoutTimer);
		navigate(AppRoutings.Root);
	};

	useEffect(() => {
		if (autoLogoutTimeLeft <= 0 && autoLogoutTimer) {
			handelLogout();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoLogoutTimeLeft]);

	const handleAutoLogoutTimer = () => {
		clearInterval(autoLogoutTimer);
		setAutoLogoutTimer(
			setInterval(() => {
				setAutoLogoutTimeLeft((autoLogoutTimeLeft) => autoLogoutTimeLeft - 1);
			}, 1000)
		);
	};

	const startInActivityTimer = () => {
		if (inActivityTimer) {
			clearTimeout(inActivityTimer);
		}
		const resetSessionTimer = localStorage.getItem(
			Shared.localStorageKeys.resetSessionTimer
		);
		if (resetSessionTimer === TimerResetEnum.Yes) {
			setOpenConfirmationDialog(false);
			setLogout(false);
			clearInterval(autoLogoutTimer);
			setAutoLogoutTimeLeft(inActivityCheckInterval);
			setTimeout(() => {
				localStorage.setItem(
					Shared.localStorageKeys.resetSessionTimer,
					TimerResetEnum.No
				);
			}, 1000);
		}
		inActivityTimer = setTimeout(() => {
			let lastInteractionTime = localStorage.getItem(
				Shared.localStorageKeys.lastInteractionTime
			);
			if (lastInteractionTime) {
				const diff = differenceInSeconds(
					new Date(),
					new Date(lastInteractionTime)
				);
				if (isLogout) {
					clearTimeout(inActivityTimer);
				} else {
					if (diff < idleTimeLimit * 60 - inActivityCheckInterval) {
						startInActivityTimer();
					} else {
						setOpenConfirmationDialog(true);
						handleAutoLogoutTimer();
					}
				}
			} else {
				handelLogout();
			}
		}, 1000);
	};

	const addEvents = () => {
		events.forEach((eventName) => {
			window.addEventListener(eventName, eventHandler);
		});
		startInActivityTimer();
	};

	const removeEvents = () => {
		events.forEach((eventName) => {
			window.removeEventListener(eventName, eventHandler);
		});
	};

	const handleConfirmationDialogClose = async (isConfirm = false) => {
		setOpenConfirmationDialog(false);
		if (isConfirm) {
			// handle "Stay Connected" button action
			localStorage.setItem(
				Shared.localStorageKeys.resetSessionTimer,
				TimerResetEnum.Yes
			);
			setLogout(false);
			clearInterval(autoLogoutTimer);
			startInActivityTimer();
			setAutoLogoutTimeLeft(inActivityCheckInterval);
			MsalAuthService.getAuthToken();
		} else {
			// handle "Logout Now" button action
			handelLogout();
		}
	};

	return openConfirmationDialog ? (
		<ConfirmationDialog
			open={openConfirmationDialog}
			title="Session Timeout"
			message={
				<CustomHTMLMessage
					message={`You will be automatically logged out in <span style="font-weight: bolder;">(${autoLogoutTimeLeft})</span> ${
						autoLogoutTimeLeft <= 9 ? "second" : "seconds"
					}.`}
				/>
			}
			positiveActionText="Stay Logged-In"
			negativeActionText="Logout Now"
			showTitleCloseIcon={false}
			handleDialogClose={handleConfirmationDialogClose}
		/>
	) : (
		<></>
	);
};
